import { createSlice } from '@reduxjs/toolkit';
import { LegalDocument } from '@/services/api.types';
import legalApi from '@/services/legal.api';

// Legal documents for the sidebar dialog (batch 004, ADR-5).
//
// Why a slice at all: the list is fetched at the ConfigGate/bootstrapConfig layer
// so the sidebar links are present on its first frame rather than appearing a beat
// later — this repo has an explicit no-flash stance (sidebar.tsx:261-265), and the
// behaviour being replaced blocked the whole app render on the tenant custom-info
// request. A mutation's result is not addressable from the cache, so the slice is
// the single copy every consumer reads. Mirrors the runtime-config three-piece
// pattern (store/configuration.ts).
export type LegalState = {
  documents: LegalDocument[];
  // Whether a fetch has been STARTED this session — in flight, or settled either
  // way. This is NOT `documents.length === 0`, and it is not "settled" either; all
  // three distinctions are load-bearing:
  //   - an EMPTY list is the correct, normal answer for a tenant with no legal
  //     documents configured (contract §2), so an emptiness guard would re-fire the
  //     request on every render for exactly those tenants;
  //   - a failed fetch must count, or a 5xx becomes a retry loop;
  //   - IN-FLIGHT must count, or concurrent callers all see `false` and each fire.
  //     The integration gate measured exactly that: three identical requests in one
  //     session (bootstrap + authed-container + a re-render) because the flag only
  //     flipped once a response had landed. Harmless on an idempotent GET, but it is
  //     still three round trips for one piece of session state.
  // Consumers use it to decide whether to trigger, never to decide what to show:
  // no documents means no entries, silently, in every case.
  attempted: boolean;
  // The access token the recorded attempt was made WITH ('' = never attempted).
  //
  // `attempted` on its own answers "did this page ask", and that is a weaker claim
  // than the guard needs. The flag outlives the credential that set it, and one
  // caller sets it while NOT logged in: index.tsx bootstrapLegalDocuments() fires for
  // any token sitting in localStorage and only checks that one EXISTS. Open /login
  // directly with a token left over from a previous session and that request 401s —
  // legal.api rides base.query, which has no 401 interceptor and no full-page bounce,
  // so nothing resets the store. The user then logs in inside the same document
  // (advanceLoginStep -> navigate('/')), authed-container's guard saw the stale
  // claim, asked nothing, and the sidebar showed neither Terms of Service nor Privacy
  // Policy until a reload.
  //
  // Pinning the attempt to its token makes the claim say what the guard means: THIS
  // credential has already asked. A different token is a different authenticated
  // session, so it gets to ask once of its own.
  attemptedToken: string;
  // The wire language (`English` / `Danish`) the recorded attempt asked in
  // ('' = never attempted). PD-3030.
  //
  // The list is per language, and the profile page lets a signed-in clinician
  // change language without a new token. A claim naming only the token would
  // answer "already asked" for a language nobody asked in, and the sidebar would
  // keep offering the documents of the language the session started in. So the
  // claim is (token, language): the same pair still asks exactly once.
  //
  // It is also the slot that decides which response is current — see
  // matchFulfilled.
  attemptedLang: string;
};

const initialState: LegalState = {
  documents: [],
  attempted: false,
  attemptedToken: '',
  attemptedLang: '',
};

const slice = createSlice({
  name: 'legal',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // Claim the attempt the moment a request goes out, so a second caller during
      // the round trip sees the claim already made and does not duplicate it.
      //
      // The token is read off the request's own arguments rather than passed in by
      // the caller: the two trigger sites (bootstrap and authed-container) would
      // otherwise each have to remember to keep the claim and the Bearer in step, and
      // the whole defect this records was a claim that outlived its credential.
      .addMatcher(
        legalApi.endpoints.legalDocuments.matchPending,
        (state, { meta }) => {
          state.attempted = true;
          state.attemptedToken = meta.arg.originalArgs?.token ?? '';
          state.attemptedLang = meta.arg.originalArgs?.lang ?? '';
        }
      )
      .addMatcher(
        legalApi.endpoints.legalDocuments.matchFulfilled,
        (state, { payload, meta }) => {
          // Apply only the answer in the language of the LATEST claim (PD-3030).
          // Two language switches inside one round trip put two requests in
          // flight, and responses can land in either order: applying whichever
          // lands last could leave the English list behind a Danish UI, and the
          // sidebar would open the English documents.
          //
          // Compared on language only, not on token: the list's content depends
          // on the language, not on which of the user's tokens fetched it.
          // Comparing the token too would drop a correct list in the renewal race
          // (an old-token answer landing after the new-token request failed).
          if (meta.arg.originalArgs?.lang !== state.attemptedLang) {
            return;
          }
          // protojson omits an empty repeated field rather than sending [], so a
          // tenant with no documents yields `{}` — normalize to an array here so no
          // consumer has to.
          state.documents = payload?.documents ?? [];
          state.attempted = true;
        }
      )
      .addMatcher(legalApi.endpoints.legalDocuments.matchRejected, state => {
        // Fail closed and SILENT: mark the attempt so nothing retries, and leave
        // any list already loaded alone — dropping a good list because a later
        // refetch failed would make the links vanish mid-session for no reason the
        // user could understand. No toast: an unreachable legal endpoint is not
        // something the clinician can act on, and the sidebar simply shows nothing.
        state.attempted = true;
      });
  },
});

export const selectLegalDocuments = (state: {
  legal: LegalState;
}): LegalDocument[] => state.legal.documents;

export const selectLegalFetchAttempted = (state: {
  legal: LegalState;
}): boolean => state.legal.attempted;

// Whether THIS token's session has already asked IN THIS LANGUAGE. This is the one
// the trigger sites guard on; the flag above stays for the slice's own tests and for
// anything that only wants to know whether a request was ever made.
//
// An empty token never matches a recorded attempt, so a caller with no credential
// cannot be waved through by someone else's claim. `lang` is the WIRE value
// (legalContentLang), and it is required on purpose: an optional language would let
// a caller silently fall back to the token-only claim PD-3030 replaced.
export const selectLegalFetchAttemptedFor = (
  state: { legal: LegalState },
  token: string,
  lang: string
): boolean =>
  state.legal.attempted &&
  !!token &&
  state.legal.attemptedToken === token &&
  state.legal.attemptedLang === lang;

// Convenience for the sidebar's per-type gating (approved visible change #4: show
// exactly the types the list contains, instead of one switch gating both links).
export const selectLegalDocumentByType = (
  state: { legal: LegalState },
  type: string
): LegalDocument | undefined =>
  state.legal.documents.find(doc => doc.type === type);

export default slice.reducer;
